import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { api } from '../../services/api';
import { DailyCollection, fmtCurrency } from './types';

interface ReturnDialogProps {
  open: boolean;
  collection: DailyCollection | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ReturnDialog({ open, collection, onClose, onSuccess }: ReturnDialogProps) {
  const [refundAmount, setRefundAmount] = useState<string>('0');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saleTotalPaid, setSaleTotalPaid] = useState<number>(0);

  useEffect(() => {
    if (open && collection?.sale_id) {
      api.get(`/sales/${collection.sale_id}/installments`)
        .then((res) => setSaleTotalPaid(res.data.total_paid ?? 0))
        .catch(() => setSaleTotalPaid(0));
    }
  }, [open, collection?.sale_id]);

  const refundNum = parseFloat(refundAmount) || 0;

  const handleClose = () => {
    setRefundAmount('0');
    setReason('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!collection) return;
    setLoading(true);
    setError(null);
    try {
      await api.post(`/sales/${collection.sale_id}/return`, {
        refund_amount: refundNum,
        reason: reason || undefined,
      });
      handleClose();
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al procesar la devolucion');
    } finally {
      setLoading(false);
    }
  };

  if (!collection) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Devolver Producto</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Cliente</Typography>
          <Typography sx={{ fontWeight: 600 }}>{collection.customer_surname} {collection.customer_name}</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Producto</Typography>
          <Typography sx={{ fontWeight: 600 }}>{collection.product_name}</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Cuota</Typography>
          <Typography sx={{ fontWeight: 600 }}>{collection.installment_number}/{collection.total_installments} - {fmtCurrency(collection.installment_amount)}</Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">Total pagado en esta venta</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{fmtCurrency(saleTotalPaid)}</Typography>
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          La venta sera desactivada y el producto volvera a estar disponible en stock.
          Si se indica un monto de reembolso, se creara un gasto con categoria "devolucion".
        </Alert>

        <TextField
          label="Monto de reembolso"
          type="number"
          fullWidth
          value={refundAmount}
          onChange={(e) => setRefundAmount(e.target.value)}
          inputProps={{ min: 0, max: saleTotalPaid, step: 0.01 }}
          helperText={`Maximo: ${fmtCurrency(saleTotalPaid)} (total pagado en esta venta)`}
          sx={{ mb: 2 }}
        />

        <TextField
          label="Motivo (opcional)"
          fullWidth
          multiline
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || refundNum < 0 || refundNum > saleTotalPaid}
          sx={{
            backgroundColor: '#EF4444',
            '&:hover': { backgroundColor: '#DC2626' },
          }}
        >
          {loading ? <CircularProgress size={20} /> : 'Confirmar Devolucion'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
